import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { maintenanceApi, assetsApi, taskLibraryApi, workOrdersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AssetCombobox from '../components/assets/AssetCombobox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { ActionMenu } from '../components/ui/action-menu';
import { DensityToggle, SortableTh, useDensity, useSortState, clientSort } from '../components/ui/list-controls';
import { 
  format, 
  isToday, 
  isThisWeek, 
  isThisMonth, 
  startOfWeek, 
  endOfWeek, 
  subWeeks, 
  startOfMonth, 
  endOfMonth, 
  subMonths,
  isWithinInterval,
  parseISO,
  differenceInCalendarDays,
  startOfDay
} from 'date-fns';
import { 
  Loader2, 
  Calendar as CalendarIcon, 
  Plus,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Settings,
  LayoutGrid,
  List,
  Search,
  X,
  Eye,
  Trash2,
  Package,
  Wrench,
  Link as LinkIcon,
  Copy,
  Pencil,
  ArrowRight,
  Pause,
  Play,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';

const DATE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

// ── PM status engine ────────────────────────────────────────────────────────
// Everything the redesigned screen needs is derived client-side from the
// schedule + its asset. No extra backend fields beyond asset.hours_updated_at.
const PM_STALE_DAYS = 30;      // an hour-meter reading older than this = unreliable
const PM_DUE_SOON_DAYS = 7;    // time schedules due within a week
const PM_DUE_SOON_FRAC = 0.15; // usage schedules with <15% of the interval left

const nf = (n) => Number(n || 0).toLocaleString();

// Interval expressed in words. "Every 365 days" → "Annually".
const intervalLabel = (s) => {
  if (s.interval_type === 'usage_based') return `Every ${nf(s.interval_hours)} hrs`;
  const d = s.interval_days;
  const words = { 1: 'Daily', 7: 'Weekly', 14: 'Fortnightly', 30: 'Monthly', 31: 'Monthly', 90: 'Quarterly', 91: 'Quarterly', 92: 'Quarterly', 182: 'Half-yearly', 183: 'Half-yearly', 365: 'Annually', 366: 'Annually' };
  if (d && words[d]) return words[d];
  return d ? `Every ${nf(d)} days` : '—';
};

// Returns { status, mode, urgency, ... } for a schedule given its asset.
// status ∈ overdue | due_soon | on_track | reading_out_of_date | no_reading
const computeMaintStatus = (s, asset) => {
  const today = startOfDay(new Date());
  if (s.interval_type === 'time_based') {
    if (!s.next_due_date) return { status: 'on_track', mode: 'time', urgency: 9999 };
    const due = parseISO(s.next_due_date);
    const days = differenceInCalendarDays(startOfDay(due), today);
    const interval = s.interval_days || 1;
    const status = days < 0 ? 'overdue' : days <= PM_DUE_SOON_DAYS ? 'due_soon' : 'on_track';
    return { status, mode: 'time', due, days, urgency: days / interval };
  }
  // usage_based
  const nextHours = s.next_due_hours ?? ((s.last_service_hours || 0) + (s.interval_hours || 0));
  const interval = s.interval_hours || 1;
  const current = asset ? asset.hours : null;
  if (asset == null || current == null) {
    return { status: 'no_reading', mode: 'usage', nextHours, interval, urgency: 100000 };
  }
  const readAt = asset.hours_updated_at || asset.updated_at;
  const readDays = readAt ? differenceInCalendarDays(today, startOfDay(parseISO(readAt))) : null;
  const toGo = nextHours - current;
  const fracRemaining = toGo / interval;
  if (readDays != null && readDays > PM_STALE_DAYS) {
    return { status: 'reading_out_of_date', mode: 'usage', nextHours, interval, current, toGo, readDays, urgency: 90000 + fracRemaining };
  }
  const status = toGo <= 0 ? 'overdue' : fracRemaining <= PM_DUE_SOON_FRAC ? 'due_soon' : 'on_track';
  return { status, mode: 'usage', nextHours, interval, current, toGo, readDays, fracRemaining, urgency: fracRemaining };
};

const PM_STATUS_PILL = {
  overdue: { label: 'Overdue', cls: 'bg-[#FDEDE8] text-[#B4432A] border-[#F3C7BB]' },
  due_soon: { label: 'Due soon', cls: 'bg-[#FFF3C4] text-[#7A5B00] border-[#F2D97A]' },
  on_track: { label: 'On track', cls: 'bg-[#F1EFEA] text-[#6B6659] border-[#E6E2D9]' },
  reading_out_of_date: { label: 'No reading', cls: 'bg-[#F1EFEA] text-[#A8A296] border-[#E6E2D9]' },
  no_reading: { label: 'No reading', cls: 'bg-[#F1EFEA] text-[#A8A296] border-[#E6E2D9]' },
};

const PmStatusPill = ({ status }) => {
  const s = PM_STATUS_PILL[status] || PM_STATUS_PILL.on_track;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${s.cls}`} data-testid={`pm-status-${status}`}>
      {s.label}
    </span>
  );
};

// CAL (time) / HRS (usage) service-type tag.
const ServiceTypeBadge = ({ type }) => (
  <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-[4px] bg-[#F1EFEA] text-[#8A857A] text-[10px] font-bold tracking-wide shrink-0">
    {type === 'usage_based' ? 'HRS' : 'CAL'}
  </span>
);

const barColor = (status) => {
  if (status === 'overdue') return 'bg-[#D9502B]';
  if (status === 'due_soon') return 'bg-[#E9B400]';
  if (status === 'reading_out_of_date' || status === 'no_reading') return 'bg-[#C7C1B4]';
  return 'bg-[#B8B2A6]';
};

const NextDueCell = ({ st }) => {
  if (st.mode === 'time') {
    if (!st.due) return <span className="text-muted-foreground text-[13px]">No date</span>;
    const overdue = st.status === 'overdue';
    const d = st.days;
    let rel;
    if (d === 0) rel = 'today';
    else if (d === 1) rel = 'tomorrow';
    else if (d === -1) rel = '1 day over';
    else if (d < 0) rel = `${-d} days over`;
    else rel = `in ${d} days`;
    return (
      <div className="flex items-baseline gap-2">
        <span className={overdue ? 'text-[#B4432A] font-semibold' : 'text-[#2A2A2A]'}>{format(st.due, 'dd/MM/yyyy')}</span>
        <span className={`text-[12px] ${overdue ? 'text-[#B4432A]' : 'text-muted-foreground'}`}>{rel}</span>
      </div>
    );
  }
  if (st.status === 'no_reading') {
    return <span className="text-[13px] text-muted-foreground">No hour-meter reading on asset</span>;
  }
  const overdue = st.status === 'overdue';
  const pct = st.nextHours ? Math.max(0, Math.min(100, (st.current / st.nextHours) * 100)) : 0;
  const relTxt = st.toGo <= 0 ? `${nf(Math.abs(Math.round(st.toGo)))} over` : `${nf(Math.round(st.toGo))} hrs to go`;
  return (
    <div className="w-[230px] max-w-full">
      <div className="flex items-baseline gap-1.5 text-[13px]">
        <span className={overdue ? 'text-[#B4432A] font-semibold' : 'text-[#2A2A2A] font-medium'}>{nf(Math.round(st.current))}</span>
        <span className="text-muted-foreground">/ {nf(Math.round(st.nextHours))} hrs</span>
        <span className={`text-[12px] ml-1 ${overdue ? 'text-[#B4432A]' : st.status === 'due_soon' ? 'text-[#7A5B00]' : 'text-muted-foreground'}`}>{relTxt}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[#EDEAE2] overflow-hidden">
        <div className={`h-full rounded-full ${barColor(st.status)}`} style={{ width: `${pct}%` }} />
      </div>
      {st.status === 'reading_out_of_date' ? (
        <div className="mt-1 text-[11px] text-[#B4772A]">Hour meter last read {st.readDays} days ago — status unreliable</div>
      ) : st.readDays != null ? (
        <div className="mt-1 text-[11px] text-muted-foreground">Hour meter read {st.readDays === 0 ? 'today' : `${st.readDays} day${st.readDays === 1 ? '' : 's'} ago`}</div>
      ) : null}
    </div>
  );
};


// A single PM schedule row (40px). Used by both Due-next and By-asset views.
const PmRow = ({ row, onOpen, onRaise, selected, onToggleSelect, selectDisabled }) => {
  const { s, asset, st } = row;
  const overdue = st.status === 'overdue';
  return (
    <tr
      className="border-b border-[#F0EDE6] hover:bg-secondary/40 cursor-pointer transition-colors"
      onClick={() => onOpen(s)}
      data-testid={`schedule-row-${s.id}`}
    >
      <td className="pl-4 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={!!selected}
          disabled={selectDisabled}
          onChange={() => onToggleSelect(row)}
          className="w-4 h-4 rounded border-[#CFC8B8] accent-[#FFD108] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          title={selectDisabled ? 'Select schedules from one asset at a time' : 'Select'}
          data-testid={`pm-select-${s.id}`}
        />
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ServiceTypeBadge type={s.interval_type} />
          <span className="font-medium text-[#2A2A2A] text-[13px]">{s.name}</span>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="text-[13px] text-[#2A2A2A]">{asset ? (asset.name || asset.unit_number) : 'Unknown asset'}</div>
        {asset?.unit_number && <div className="text-[11px] font-mono text-muted-foreground">{asset.unit_number}</div>}
      </td>
      <td className="px-4 py-2.5 text-[13px] text-[#59554D]">{intervalLabel(s)}</td>
      <td className="px-4 py-2.5"><NextDueCell st={st} /></td>
      <td className="px-4 py-2.5"><PmStatusPill status={st.status} /></td>
      <td className="px-4 py-2.5 text-right">
        {s.open_work_order_id ? (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onRaise(row); }} data-testid={`pm-view-wo-${s.id}`}>
            <Wrench className="w-3.5 h-3.5 mr-1.5" />
            View WO
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onRaise(row); }} data-testid={`pm-raise-wo-${s.id}`}>
            <Wrench className="w-3.5 h-3.5 mr-1.5" />
            Raise WO
          </Button>
        )}
      </td>
    </tr>
  );
};



// Maintenance Form Component
const MaintenanceForm = ({ assets, onSuccess, onCancel }) => {
  const { formatDate } = useSettings();
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    asset_id: '',
    name: '',
    interval_type: 'time_based',
    interval_days: 90,
    interval_hours: 250,
    last_service_hours: 0,
    task_template_id: '',
  });

  // Task Library state
  const [taskTemplates, setTaskTemplates] = useState([]);

  // Bulk-mode state — assets grouped by category, with per-asset overrides
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkTargets, setBulkTargets] = useState({}); // { [asset_id]: { selected, last_service_hours, last_service_date } }

  useEffect(() => {
    taskLibraryApi.list({ active_only: true })
      .then((r) => setTaskTemplates(r.data || []))
      .catch(() => setTaskTemplates([]));
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    assets.forEach(a => { if (a.category) set.add(a.category); });
    return Array.from(set).sort();
  }, [assets]);

  const filteredAssets = useMemo(
    () => assets.filter(a => !bulkCategory || a.category === bulkCategory),
    [assets, bulkCategory]
  );

  const selectedBulkCount = useMemo(
    () => Object.values(bulkTargets).filter(t => t?.selected).length,
    [bulkTargets]
  );

  const toggleBulkTarget = (assetId, checked) => {
    setBulkTargets(prev => ({
      ...prev,
      [assetId]: { ...(prev[assetId] || {}), selected: checked },
    }));
  };

  const setBulkTargetField = (assetId, field, value) => {
    setBulkTargets(prev => ({
      ...prev,
      [assetId]: { ...(prev[assetId] || { selected: true }), [field]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const baseInterval = {
      interval_days: formData.interval_type === 'time_based' ? formData.interval_days : null,
      interval_hours: formData.interval_type === 'usage_based' ? formData.interval_hours : null,
    };
    const taskTpl = taskTemplates.find(t => t.id === formData.task_template_id);

    setSubmitting(true);
    try {
      if (mode === 'single') {
        if (!formData.asset_id) {
          toast.error('Please select an asset');
          setSubmitting(false);
          return;
        }
        if (formData.interval_type === 'time_based' && !date) {
          toast.error('Last Service Date is required for time-based schedules');
          setSubmitting(false);
          return;
        }
        if (formData.interval_type === 'usage_based' && (formData.last_service_hours === '' || formData.last_service_hours == null || Number.isNaN(formData.last_service_hours))) {
          toast.error('Last Service Hours is required for usage-based schedules');
          setSubmitting(false);
          return;
        }
        await maintenanceApi.create({
          asset_id: formData.asset_id,
          name: formData.name,
          interval_type: formData.interval_type,
          ...baseInterval,
          last_service_date: date ? date.toISOString() : null,
          last_service_hours: formData.interval_type === 'usage_based' ? formData.last_service_hours : null,
          task_template_id: formData.task_template_id || null,
          task_template_name: taskTpl?.name || null,
        });
        toast.success('Maintenance schedule created');
        onSuccess();
      } else {
        // Bulk
        const selectedEntries = Object.entries(bulkTargets).filter(([, t]) => t?.selected);
        if (selectedEntries.length === 0) {
          toast.error('Select at least one asset');
          setSubmitting(false);
          return;
        }
        // Per-asset validation: each selected row must carry its baseline
        const missing = [];
        const targets = selectedEntries.map(([asset_id, t]) => {
          const asset = assets.find(a => a.id === asset_id);
          const label = asset?.unit_number || asset?.name || asset?.serial_number || asset_id;
          if (formData.interval_type === 'time_based' && !t.last_service_date) {
            missing.push(label);
          }
          if (formData.interval_type === 'usage_based' && (t.last_service_hours === '' || t.last_service_hours == null)) {
            missing.push(label);
          }
          return {
            asset_id,
            last_service_date: t.last_service_date || null,
            last_service_hours:
              formData.interval_type === 'usage_based' && t.last_service_hours != null && t.last_service_hours !== ''
                ? parseFloat(t.last_service_hours)
                : null,
          };
        });
        if (missing.length > 0) {
          const fieldName = formData.interval_type === 'time_based' ? 'Last Service Date' : 'Last Service Hours';
          toast.error(`${fieldName} missing for ${missing.length} asset${missing.length === 1 ? '' : 's'}: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''}`);
          setSubmitting(false);
          return;
        }
        const res = await maintenanceApi.bulkCreate({
          name: formData.name,
          interval_type: formData.interval_type,
          ...baseInterval,
          task_template_id: formData.task_template_id || null,
          task_template_name: taskTpl?.name || null,
          targets,
        });
        const { created_count, skipped_count } = res.data;
        if (skipped_count > 0) {
          toast.success(`${created_count} schedule${created_count === 1 ? '' : 's'} created · ${skipped_count} skipped (already exist)`);
        } else {
          toast.success(`${created_count} schedule${created_count === 1 ? '' : 's'} created across selected assets`);
        }
        onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="id-card p-6 space-y-6">
      {/* Mode toggle */}
      <div className="flex p-1 bg-muted/30 border border-border rounded-sm" data-testid="schedule-mode-tabs">
        {[
          { v: 'single', label: 'Single asset' },
          { v: 'bulk', label: 'Bulk apply' },
        ].map(m => (
          <button
            key={m.v}
            type="button"
            onClick={() => setMode(m.v)}
            className={`flex-1 px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm transition-colors ${
              mode === m.v ? 'bg-[#FFD108] text-black font-bold' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`schedule-mode-${m.v}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'single' ? (
        <div className="space-y-2">
          <Label>Asset *</Label>
          <AssetCombobox
            assets={assets}
            value={formData.asset_id || null}
            onChange={(v) => setFormData(prev => ({ ...prev, asset_id: v || '' }))}
            placeholder="Select asset"
            className="w-full"
            testId="maintenance-asset-select"
          />
        </div>
      ) : (
        <div className="space-y-3" data-testid="bulk-asset-selector">
          <div className="space-y-2">
            <Label>Asset Category *</Label>
            <Select value={bulkCategory || 'unset'} onValueChange={(v) => setBulkCategory(v === 'unset' ? '' : v)}>
              <SelectTrigger data-testid="bulk-category-select">
                <SelectValue placeholder="Filter assets by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">— Select category —</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Tip: pick a category so you only see machines that share this service interval (e.g. all Excavators).
              {' '}
              {formData.interval_type === 'time_based'
                ? 'Each selected asset must have its own Last Service Date — they typically differ.'
                : 'Each selected asset must have its own Last Service Hours — they typically differ.'}
            </p>
          </div>

          {bulkCategory && (
            <div className="rounded-sm border border-border">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/20 border-b border-border">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">
                  {filteredAssets.length} {bulkCategory.toLowerCase()}{filteredAssets.length === 1 ? '' : 's'} · {selectedBulkCount} selected
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const allSelected = filteredAssets.every(a => bulkTargets[a.id]?.selected);
                    setBulkTargets(prev => {
                      const next = { ...prev };
                      filteredAssets.forEach(a => {
                        next[a.id] = { ...(next[a.id] || {}), selected: !allSelected };
                      });
                      return next;
                    });
                  }}
                  data-testid="bulk-toggle-all"
                >
                  {filteredAssets.every(a => bulkTargets[a.id]?.selected) ? 'Clear all' : 'Select all'}
                </Button>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {filteredAssets.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">No assets in this category.</div>
                )}
                {filteredAssets.map(a => {
                  const t = bulkTargets[a.id] || {};
                  return (
                    <div key={a.id} className="flex items-start gap-3 p-3 hover:bg-muted/10" data-testid={`bulk-row-${a.id}`}>
                      <Checkbox
                        checked={!!t.selected}
                        onCheckedChange={(v) => toggleBulkTarget(a.id, !!v)}
                        className="mt-1"
                        data-testid={`bulk-check-${a.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">
                          {a.unit_number || a.name} <span className="font-mono text-xs text-muted-foreground">· {a.serial_number || ''}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {[a.make, a.model].filter(Boolean).join(' ')}
                          {a.hours != null && <span className="ml-2 font-mono">· {a.hours.toLocaleString()} h</span>}
                        </div>
                        {t.selected && (
                          <div className="mt-2 space-y-1">
                            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                              {formData.interval_type === 'usage_based' ? 'Last Service Hours *' : 'Last Service Date *'}
                            </Label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {formData.interval_type === 'usage_based' ? (
                                <Input
                                  type="number"
                                  placeholder={`Hours at last service (current: ${a.hours || 0})`}
                                  value={t.last_service_hours ?? ''}
                                  onChange={(e) => setBulkTargetField(a.id, 'last_service_hours', e.target.value)}
                                  className={`h-8 text-xs ${(t.last_service_hours === '' || t.last_service_hours == null) ? 'border-destructive/60' : ''}`}
                                  data-testid={`bulk-last-hours-${a.id}`}
                                  required
                                />
                              ) : (
                                <Input
                                  type="date"
                                  value={t.last_service_date ? t.last_service_date.slice(0, 10) : ''}
                                  onChange={(e) => setBulkTargetField(a.id, 'last_service_date', e.target.value ? new Date(e.target.value).toISOString() : '')}
                                  className={`h-8 text-xs ${!t.last_service_date ? 'border-destructive/60' : ''}`}
                                  data-testid={`bulk-last-date-${a.id}`}
                                  required
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="name">Service Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. 250hr Service, Quarterly Oil Change"
          required
          data-testid="maintenance-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Work Order Task Template (optional)</Label>
        <Select
          value={formData.task_template_id || 'unset'}
          onValueChange={(v) => setFormData(prev => ({ ...prev, task_template_id: v === 'unset' ? '' : v }))}
        >
          <SelectTrigger data-testid="task-template-select">
            <SelectValue placeholder="None — mechanic adds tasks at WO creation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unset">— None —</SelectItem>
            {taskTemplates.map(t => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {t.category ? ` · ${t.category}` : ''}
                {t.steps?.length ? ` (${t.steps.length} steps)` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          When this schedule fires a work order, its steps are copied from this template.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Interval Type *</Label>
        <RadioGroup
          value={formData.interval_type}
          onValueChange={(v) => setFormData(prev => ({ ...prev, interval_type: v }))}
          className="flex gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="time_based" id="time_based" />
            <Label htmlFor="time_based" className="flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" /> Time Based
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="usage_based" id="usage_based" />
            <Label htmlFor="usage_based" className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> Usage Based
            </Label>
          </div>
        </RadioGroup>
      </div>

      {formData.interval_type === 'time_based' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="interval_days">Interval (Days) *</Label>
            <Input
              id="interval_days"
              type="number"
              value={formData.interval_days}
              onChange={(e) => setFormData(prev => ({ ...prev, interval_days: parseInt(e.target.value) }))}
              min="1"
              required
              data-testid="interval-days-input"
            />
          </div>
          {mode === 'single' && (
            <div className="space-y-2">
              <Label>Last Service Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="last-service-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? formatDate(date) : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                When was this service last performed? Required so "Next Due" is calculated from a real baseline.
              </p>
            </div>
          )}
        </>
      )}

      {formData.interval_type === 'usage_based' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="interval_hours">Interval (Hours) *</Label>
            <Input
              id="interval_hours"
              type="number"
              value={formData.interval_hours}
              onChange={(e) => setFormData(prev => ({ ...prev, interval_hours: parseFloat(e.target.value) }))}
              min="1"
              step="0.1"
              required
              data-testid="interval-hours-input"
            />
          </div>
          {mode === 'single' && (
            <div className="space-y-2">
              <Label htmlFor="last_service_hours">Last Service Hours *</Label>
              <Input
                id="last_service_hours"
                type="number"
                value={formData.last_service_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, last_service_hours: parseFloat(e.target.value) }))}
                min="0"
                step="0.1"
                required
                data-testid="last-service-hours-input"
              />
              <p className="text-xs text-muted-foreground">
                Engine/SMU hours at the last completed service. Enter 0 for a brand-new asset.
              </p>
            </div>
          )}
        </>
      )}

      </div>

      <div className="flex gap-4 pt-4">
        <Button type="submit" className="flex-1 btn-industrial" disabled={submitting} data-testid="submit-maintenance-btn">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          {submitting
            ? 'Creating...'
            : mode === 'bulk'
              ? `Create ${selectedBulkCount || ''} Schedule${selectedBulkCount === 1 ? '' : 's'}`
              : 'Create Schedule'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Maintenance Detail View Component
const fmtHrs = (h) => (h == null ? '—' : `${Number(h).toLocaleString()} h`);

const MaintenanceDetailView = ({ schedule, onClose, onDelete, onChanged, isAdmin }) => {
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bypassOpen, setBypassOpen] = useState(false);
  const [bypassReason, setBypassReason] = useState('');
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', interval: '', last_date: '', last_hours: '' });
  const [openWorkOrders, setOpenWorkOrders] = useState([]);
  const [linkingToWO, setLinkingToWO] = useState(false);

  const refetch = async () => {
    try {
      const res = await maintenanceApi.getById(schedule.id);
      setDetail(res.data);
    } catch (e) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, [schedule.id]);

  useEffect(() => {
    if (!detail?.asset_id) return;
    workOrdersApi.getOpenForAsset(detail.asset_id)
      .then(res => setOpenWorkOrders(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => setOpenWorkOrders([]));
  }, [detail?.asset_id, detail?.open_work_order]);

  const handleLinkToWorkOrder = async (woId) => {
    setLinkingToWO(true);
    try {
      await workOrdersApi.linkMaintenance(woId, schedule.id);
      toast.success('Added to work order');
      await refetch();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to work order');
    } finally {
      setLinkingToWO(false);
    }
  };

  if (loading || !detail) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const d = detail;
  const usage = d.interval_type === 'usage_based';
  const paused = d.status === 'paused';
  const overdue = d.is_overdue;
  const asset = d.asset || {};
  const currentHours = d.current_hours ?? 0;
  const interval = usage ? (d.interval_hours || 0) : (d.interval_days || 0);
  const checklist = d.task_template_name;

  // Overdue metrics
  let daysOverdue = 0, cyclesMissed = 0, hoursOver = 0;
  if (usage && d.next_due_hours != null) {
    hoursOver = Math.max(0, Math.round(currentHours - d.next_due_hours));
  } else if (d.next_due_date) {
    daysOverdue = Math.max(0, differenceInCalendarDays(new Date(), parseISO(d.next_due_date)));
    cyclesMissed = d.interval_days ? Math.floor(daysOverdue / d.interval_days) : daysOverdue;
  }

  const cycles = d.cycles || [];
  const cyclesDone = cycles.filter(c => c.completed).length;
  const services = d.services || [];
  const last4 = services.slice(0, 4);
  const lateCount = last4.filter(s => (s.late_hours || 0) > 0).length;
  const allLate = last4.length >= 2 && lateCount === last4.length;
  const openWo = d.open_work_order;
  const leadSet = d.lead_time_days || d.lead_time_hours;

  // Bypass target
  const bypassTarget = usage
    ? `${(Math.round(currentHours) + interval).toLocaleString()} h`
    : formatDate(new Date(Date.now() + interval * 86400000).toISOString());
  // WO sign-off target
  const woTarget = usage
    ? `${((d.next_due_hours || 0) + interval).toLocaleString()} h`
    : formatDate(new Date((d.next_due_date ? parseISO(d.next_due_date).getTime() : Date.now()) + interval * 86400000).toISOString());

  // Meter bar (usage)
  const serviced = d.last_service_hours ?? 0;
  const due = d.next_due_hours ?? serviced;
  const rangeEnd = Math.max(due, currentHours);
  const span = Math.max(1, rangeEnd - serviced);
  const duePct = Math.min(100, Math.max(0, ((due - serviced) / span) * 100));
  const curPct = Math.min(100, Math.max(0, ((currentHours - serviced) / span) * 100));

  const handleCreateWorkOrder = () => {
    const params = new URLSearchParams({
      new: 'true', maintenance: d.id, asset: d.asset_id,
      title: `PM: ${d.name}`,
      description: `Preventative maintenance: ${d.name}. ${usage ? `Every ${d.interval_hours} hours` : `Every ${d.interval_days} days`}`,
    });
    navigate(`/work-orders?${params.toString()}`);
  };

  const doAction = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); if (successMsg) toast.success(successMsg); await refetch(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const submitBypass = async () => {
    if (!bypassReason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await maintenanceApi.bypass(d.id, { reason: bypassReason.trim() });
      setBypassOpen(false); setBypassReason('');
      toast.success('Cycle marked complete');
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };
  const submitPause = async () => {
    await doAction(() => maintenanceApi.pause(d.id, { reason: pauseReason.trim() || null }), 'Schedule paused');
    setPauseOpen(false); setPauseReason('');
  };
  const openEdit = () => {
    setEditForm({
      name: d.name || '',
      interval: usage ? (d.interval_hours ?? '') : (d.interval_days ?? ''),
      last_date: d.last_service_date ? d.last_service_date.slice(0, 10) : '',
      last_hours: d.last_service_hours ?? '',
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    const payload = { name: editForm.name };
    if (usage) {
      payload.interval_hours = Number(editForm.interval) || 0;
      if (editForm.last_hours !== '') payload.last_service_hours = Number(editForm.last_hours);
    } else {
      payload.interval_days = Number(editForm.interval) || 0;
      if (editForm.last_date) payload.last_service_date = new Date(editForm.last_date).toISOString();
    }
    await doAction(() => maintenanceApi.update(d.id, payload), 'Schedule updated');
    setEditOpen(false);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="maintenance-detail">
      {/* Title block */}
      <div>
        <div className="flex items-center gap-2.5">
          <span className="px-1.5 py-0.5 rounded bg-[#EDEAE2] text-[#6B6558] text-[10px] font-bold tracking-wide">PM</span>
          <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{d.name}</h2>
        </div>
        <div className="mt-2 flex items-center gap-2.5 flex-wrap text-sm">
          {paused ? (
            <span className="px-2 py-0.5 rounded-full bg-[#EDEAE2] text-[#6B6558] text-[11px] font-bold uppercase tracking-wide" data-testid="pm-status-paused">Paused</span>
          ) : overdue ? (
            <span className="px-2 py-0.5 rounded-full bg-[#FDEDE8] text-[#B4432A] border border-[#F3C7BB] text-[11px] font-bold uppercase tracking-wide" data-testid="pm-status-overdue">Overdue</span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-[#EAF3EA] text-[#2E7D32] border border-[#CBE3CB] text-[11px] font-bold uppercase tracking-wide">On track</span>
          )}
          <span className="text-[#6B6558]">
            {usage ? `Due at ${fmtHrs(d.next_due_hours)}` : `Due ${d.next_due_date ? formatDate(d.next_due_date) : '—'}`}
          </span>
          {!paused && overdue && (
            <span className="text-[#B4432A] font-semibold" data-testid="pm-overdue-detail">
              {usage
                ? `${hoursOver} h over · ${Number(currentHours).toLocaleString()} h now`
                : `${daysOverdue} days overdue · ${cyclesMissed} cycles missed`}
            </span>
          )}
        </div>
      </div>

      {/* Asset card */}
      <div className="id-card p-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#F3EFE6] flex items-center justify-center shrink-0"><Package className="w-5 h-5 text-[#C9A400]" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Asset</div>
            <div className="font-semibold text-[#1A1A1A] truncate">{asset.name || 'Unknown'}</div>
            <div className="text-xs text-[#8A857A] font-mono">{asset.serial_number || asset.unit_number || '—'} · {usage ? `${Number(currentHours).toLocaleString()} h` : (asset.status || 'active')}</div>
          </div>
          <button onClick={() => navigate(`/assets?view=${d.asset_id}`)} className="text-[#C9A400] font-semibold text-sm inline-flex items-center gap-1 shrink-0" data-testid="pm-open-asset">Open asset <ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Schedule card */}
      <div className="id-card p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Schedule</div>
          {isAdmin && <Button variant="outline" size="sm" onClick={openEdit} data-testid="pm-edit-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>}
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <div className="text-xs text-[#8A857A] mb-0.5">Trigger</div>
            <div className="font-semibold text-[#1A1A1A]">{usage ? `Usage based · every ${d.interval_hours} hours` : `Time based · every ${d.interval_days} day${d.interval_days === 1 ? '' : 's'}`}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A857A] mb-0.5">Checklist</div>
            <div className="font-semibold text-[#C9A400]">{checklist || 'None set'}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A857A] mb-0.5">Next due</div>
            <div className={`font-semibold ${overdue && !paused ? 'text-[#B4432A]' : 'text-[#1A1A1A]'}`}>{usage ? fmtHrs(d.next_due_hours) : (d.next_due_date ? formatDate(d.next_due_date) : '—')}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A857A] mb-0.5">{usage ? 'Last service' : 'Last completed'}</div>
            <div className="font-semibold text-[#1A1A1A]">{usage ? `${fmtHrs(d.last_service_hours)}${d.last_service_date ? ` · ${formatDate(d.last_service_date)}` : ''}` : (d.last_service_date ? formatDate(d.last_service_date) : '—')}</div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-[#EDEAE2] text-xs text-[#8A857A]">
          {d.created_by_name ? `Created by ${d.created_by_name}` : 'Created'}{d.created_at ? ` · ${format(parseISO(d.created_at), 'MM/yyyy')}` : ''} · {leadSet ? `alerts ${d.lead_time_hours ? `${d.lead_time_hours} h` : `${d.lead_time_days} days`} before due` : 'no advance warning set'}
        </div>
      </div>

      {/* Meter (usage) */}
      {usage && (
        <div className="id-card p-4" data-testid="pm-meter">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Meter</div>
            <div className="text-lg font-bold text-[#1A1A1A]">{Number(currentHours).toLocaleString()} h</div>
          </div>
          <div className="h-2.5 rounded-full bg-[#EDEAE2] overflow-hidden relative">
            <div className="absolute inset-y-0 left-0 bg-[#FFD108]" style={{ width: `${overdue ? duePct : curPct}%` }} />
            {overdue && <div className="absolute inset-y-0 bg-[#D4462A]" style={{ left: `${duePct}%`, width: `${Math.max(0, curPct - duePct)}%` }} />}
            <div className="absolute inset-y-0 w-0.5 bg-[#1A1A1A]" style={{ left: `${duePct}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className="text-[#8A857A]">{Number(serviced).toLocaleString()} h serviced</span>
            <span className="text-[#1A1A1A] font-semibold">{Number(due).toLocaleString()} h due {overdue && <span className="text-[#B4432A]">+{hoursOver} h</span>}</span>
          </div>
        </div>
      )}

      {/* Compliance strip (time) */}
      {!usage && cycles.length > 0 && (
        <div className="id-card p-4" data-testid="pm-compliance">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Compliance</div>
            <div className={`text-sm font-semibold ${cyclesDone < cycles.length ? 'text-[#B4432A]' : 'text-[#2E7D32]'}`}>{cyclesDone} of last {cycles.length} cycles completed</div>
          </div>
          <div className="flex gap-[3px] flex-wrap">
            {cycles.map((c, i) => (
              <div key={i} title={formatDate(c.date)} className={`w-[18px] h-6 rounded-[3px] border ${c.completed ? 'bg-[#EAF3EA] border-[#CBE3CB]' : 'bg-[#FDEDE8] border-[#F3C7BB]'}`} />
            ))}
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-[#8A857A]"><span>{cycles.length} cycles ago</span><span>Today</span></div>
        </div>
      )}

      {/* Service history (usage) */}
      {usage && services.length > 0 && (
        <div className="id-card p-4" data-testid="pm-service-history">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Service history</div>
            {last4.length > 0 && <div className={`text-sm font-semibold ${lateCount > 0 ? 'text-[#B4432A]' : 'text-[#2E7D32]'}`}>{lateCount} of last {last4.length} services late</div>}
          </div>
          <div className="flex flex-col divide-y divide-[#EDEAE2]">
            {services.map((s, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="font-mono text-[#6B6558] w-24 shrink-0">{s.date ? formatDate(s.date) : '—'}</span>
                <span className="flex-1 text-[#8A857A]">due {Number(s.due_hours ?? 0).toLocaleString()} h · done {Number(s.done_hours ?? 0).toLocaleString()} h</span>
                {s.late_hours > 0 && <span className="px-2 py-0.5 rounded bg-[#FFF3C4] text-[#7A5B00] border border-[#F2D97A] text-xs font-bold shrink-0">+{s.late_hours} h</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insight / Pause */}
      {paused ? (
        <div className="id-card p-4 bg-[#FBFAF6]" data-testid="pm-resume-card">
          <div className="flex items-center gap-2 mb-1"><Pause className="w-4 h-4 text-[#C9A400]" /><span className="font-semibold text-[#1A1A1A]">Schedule is paused</span></div>
          <p className="text-sm text-[#6B6558] mb-3">Cycles are not accruing while this asset is out of service.{d.pause_reason ? ` Reason: ${d.pause_reason}.` : ''} Resume when it returns to work — the first cycle becomes due from the next reading.</p>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => doAction(() => maintenanceApi.resume(d.id), 'Schedule resumed')} data-testid="pm-resume-btn"><Play className="w-3.5 h-3.5 mr-1" />Resume schedule</Button>
        </div>
      ) : usage && allLate && !leadSet ? (
        <div className="rounded-xl border border-[#F2D97A] bg-[#FFFBEB] p-5" data-testid="pm-insight-late">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-[#C9A400]" /><span className="font-semibold text-[#7A5B00]">Every service on this schedule has been late</span></div>
          <p className="text-sm text-[#6B6558] mb-3">The pattern is consistent. A lead threshold would raise the work order before the reading is passed, instead of after.</p>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => doAction(() => maintenanceApi.setLeadTime(d.id, { lead_time_hours: 40 }), 'Advance warning set')} data-testid="pm-set-lead-btn">Alert 40 hours before due</Button>
        </div>
      ) : !usage && overdue ? (
        <div className="rounded-xl border border-[#F2D97A] bg-[#FFFBEB] p-5" data-testid="pm-insight-pause">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-[#C9A400]" /><span className="font-semibold text-[#7A5B00]">Pause instead of chasing?</span></div>
          <p className="text-sm text-[#6B6558] mb-3">Pausing stops new cycles accruing and keeps the schedule's history. It resumes on the next meter reading or operator log, with the first cycle due that day.</p>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setPauseOpen(true)} data-testid="pm-pause-btn"><Pause className="w-3.5 h-3.5 mr-1" />Pause while asset is idle</Button>
        </div>
      ) : null}

      {/* Existing WO + primary action */}
      {openWo ? (
        <>
          <button onClick={() => navigate(`/work-orders?view=${openWo.id}`)} className="w-full text-left id-card p-4 border-l-4 border-[#C9A400] hover:bg-[#FBFAF6] transition-colors" data-testid="pm-open-wo-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-[#1A1A1A]">{openWo.reference || 'Work order'} is open for this cycle</div>
                <div className="text-xs text-[#8A857A] mt-0.5">Raised {openWo.created_at ? formatDate(openWo.created_at) : '—'} · {openWo.assigned_name || 'unassigned'} · {openWo.tasks_done}/{openWo.tasks_total} tasks complete</div>
              </div>
              <span className="text-[#C9A400] font-semibold text-sm inline-flex items-center gap-1">Open <ChevronRight className="w-4 h-4" /></span>
            </div>
          </button>
          <button onClick={() => navigate(`/work-orders?view=${openWo.id}`)} className="w-full h-13 py-3.5 rounded-lg bg-[#FFD108] text-black font-bold inline-flex items-center justify-center gap-2 hover:bg-[#EAC200] transition-colors" data-testid="pm-primary-open-wo">
            <ArrowRight className="w-4 h-4" /> OPEN {openWo.reference || 'WORK ORDER'}
          </button>
          <p className="text-xs text-[#8A857A] -mt-1">One work order already covers this cycle. Creating another would double-count the service.</p>
        </>
      ) : (
        <div className="id-card p-4" data-testid="pm-resolve-card">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-2.5">Resolve</div>
          <p className="text-sm text-[#59554D] mb-3">This schedule is not on a work order yet.</p>
          <button onClick={handleCreateWorkOrder} disabled={paused} className={`w-full h-12 rounded-lg font-bold inline-flex items-center justify-center gap-2 transition-colors ${paused ? 'bg-[#EDEAE2] text-[#A8A296] cursor-not-allowed' : 'bg-[#FFD108] text-black hover:bg-[#EAC200]'}`} data-testid="create-workorder-from-pm-btn">
            <Wrench className="w-4 h-4" /> CREATE WORK ORDER
          </button>
          <p className="text-xs text-[#8A857A] mt-2">Raises one work order for the {usage ? fmtHrs(d.next_due_hours) : 'due'} service{checklist ? `, carrying the ${checklist} checklist` : ''}. Next due moves to {woTarget} on sign-off.</p>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[#E6E2D9]" />
            <span className="text-xs text-[#8A857A] whitespace-nowrap">or add to an open one</span>
            <div className="flex-1 h-px bg-[#E6E2D9]" />
          </div>
          <Select onValueChange={handleLinkToWorkOrder} disabled={linkingToWO || paused || openWorkOrders.length === 0}>
            <SelectTrigger data-testid="pm-link-to-workorder-select">
              <SelectValue placeholder={linkingToWO ? 'Adding…' : openWorkOrders.length === 0 ? 'No open work orders for this asset' : 'Select open work order…'} />
            </SelectTrigger>
            <SelectContent>
              {openWorkOrders.map(wo => (<SelectItem key={wo.id} value={wo.id}>{wo.reference ? `${wo.reference} · ` : ''}{wo.title}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Bypass */}
      <div className="id-card p-4" data-testid="pm-bypass">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Bypass</div>
          <div className="text-xs text-[#8A857A]">Leaves no checklist record</div>
        </div>
        <p className="text-sm text-[#6B6558] mb-3">Marking complete without a work order records the {usage ? fmtHrs(d.next_due_hours) : 'due'} service as met at the current reading and moves next due to {bypassTarget}. No checklist, signature{usage ? ', parts or oil sample' : ' or defects'} are captured against it.</p>
        <Button variant="outline" className="w-full" disabled={busy || paused} onClick={() => setBypassOpen(true)} data-testid="drawer-complete-btn">
          <CheckCircle className="w-4 h-4 mr-2" /> Mark this {usage ? 'service' : 'cycle'} complete
        </Button>
      </div>

      {/* Delete footer */}
      {isAdmin && onDelete && (
        <div className="flex items-center justify-between gap-4 pt-1">
          <p className="text-xs text-muted-foreground">Deleting removes the {usage ? 'service' : 'pre-start'} history behind this asset's resale and warranty record. Pause it if the asset is out of service.</p>
          <button onClick={onDelete} className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-[#F1C6C0] text-[#B4432A] font-semibold hover:bg-[#FEF2F0] shrink-0" data-testid="maintenance-delete-btn"><Trash2 className="w-4 h-4" /> Delete</button>
        </div>
      )}

      {/* Bypass reason dialog */}
      <Dialog open={bypassOpen} onOpenChange={setBypassOpen}>
        <DialogContent data-testid="pm-bypass-dialog">
          <DialogHeader>
            <DialogTitle>Bypass this {usage ? 'service' : 'cycle'}?</DialogTitle>
            <DialogDescription>This records the cycle as met with no checklist or signature and moves next due to {bypassTarget}. A reason is required and stays on the record.</DialogDescription>
          </DialogHeader>
          <Textarea value={bypassReason} onChange={(e) => setBypassReason(e.target.value)} placeholder="Why is this being bypassed?" rows={3} data-testid="pm-bypass-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBypassOpen(false)}>Cancel</Button>
            <Button onClick={submitBypass} disabled={busy} data-testid="pm-bypass-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record bypass'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pause reason dialog */}
      <Dialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <DialogContent data-testid="pm-pause-dialog">
          <DialogHeader>
            <DialogTitle>Pause this schedule?</DialogTitle>
            <DialogDescription>Cycles stop accruing while paused. History is kept. Resume when the asset returns to service.</DialogDescription>
          </DialogHeader>
          <Textarea value={pauseReason} onChange={(e) => setPauseReason(e.target.value)} placeholder="Reason (optional) — e.g. asset parked off-hire" rows={2} data-testid="pm-pause-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseOpen(false)}>Cancel</Button>
            <Button onClick={submitPause} disabled={busy} data-testid="pm-pause-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pause schedule'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="pm-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit schedule</DialogTitle>
            <DialogDescription>Next due recalculates from the interval and last-service baseline.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid="pm-edit-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Interval ({usage ? 'hours' : 'days'})</Label>
              <Input type="number" min="1" value={editForm.interval} onChange={(e) => setEditForm(f => ({ ...f, interval: e.target.value }))} data-testid="pm-edit-interval" />
            </div>
            {usage ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Last service hours</Label>
                <Input type="number" min="0" value={editForm.last_hours} onChange={(e) => setEditForm(f => ({ ...f, last_hours: e.target.value }))} data-testid="pm-edit-last-hours" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Last completed date</Label>
                <Input type="date" value={editForm.last_date} onChange={(e) => setEditForm(f => ({ ...f, last_date: e.target.value }))} data-testid="pm-edit-last-date" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={busy} data-testid="pm-edit-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const MaintenanceListPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [density, setDensity] = useDensity('maintenance_density');
  const { sortKey, sortDir, toggleSort } = useSortState();
  const [searchQuery, setSearchQuery] = useState('');
  const [bandFilter, setBandFilter] = useState(null); // null | overdue | due_soon | on_track | reading
  const [groupBy, setGroupBy] = useState('due_next'); // 'due_next' | 'by_asset'
  const [pmVisible, setPmVisible] = useState(25);
  const PM_PAGE_SIZE = 25;
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkedFilter, setLinkedFilter] = useState('all'); // 'unlinked' | 'linked' | 'all'
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null); // 'add' | 'view'
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Bulk multi-select (constrained to a single asset)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [addToWoOpen, setAddToWoOpen] = useState(false);
  const [bulkOpenWOs, setBulkOpenWOs] = useState([]);
  const [bulkLinking, setBulkLinking] = useState(false);
  const { isAdmin } = useAuth();
  const { formatDate } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (linkedFilter !== 'all') params.linked = linkedFilter;
      const [schedulesRes, assetsRes] = await Promise.all([
        maintenanceApi.getAll(params),
        assetsApi.getAll(),
      ]);
      setSchedules(schedulesRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to load maintenance schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (scheduleId) => {
    try {
      await maintenanceApi.complete(scheduleId);
      toast.success('Maintenance marked as complete');
      loadData();
      closeDrawer();
    } catch (error) {
      toast.error('Failed to complete maintenance');
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;
    setDeleting(true);
    try {
      await maintenanceApi.delete(selectedSchedule.id);
      toast.success('Maintenance schedule deleted');
      setConfirmDeleteOpen(false);
      closeDrawer();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    } finally {
      setDeleting(false);
    }
  };

  const getAssetName = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    return asset ? asset.name : 'Unknown Asset';
  };

  const getAssetSerial = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    return asset ? asset.serial_number : '-';
  };

  const assetMap = useMemo(() => { const m = {}; assets.forEach(a => { m[a.id] = a; }); return m; }, [assets]);

  const enriched = useMemo(
    () => schedules.map(s => ({ s, asset: assetMap[s.asset_id], st: computeMaintStatus(s, assetMap[s.asset_id]) })),
    [schedules, assetMap]
  );

  const bandCounts = useMemo(() => {
    const c = { overdue: 0, due_soon: 0, on_track: 0, reading: 0 };
    enriched.forEach(({ st }) => {
      if (st.status === 'overdue') c.overdue++;
      else if (st.status === 'due_soon') c.due_soon++;
      else if (st.status === 'on_track') c.on_track++;
      else c.reading++;
    });
    return c;
  }, [enriched]);

  const overdueAssetCount = useMemo(
    () => new Set(enriched.filter(({ st }) => st.status === 'overdue').map(({ s }) => s.asset_id)).size,
    [enriched]
  );

  const visibleRows = useMemo(() => {
    let rows = enriched;
    if (bandFilter) {
      rows = rows.filter(({ st }) => bandFilter === 'reading'
        ? (st.status === 'reading_out_of_date' || st.status === 'no_reading')
        : st.status === bandFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(({ s, asset }) =>
        s.name.toLowerCase().includes(q) ||
        (asset?.name || '').toLowerCase().includes(q) ||
        (asset?.unit_number || '').toLowerCase().includes(q));
    }
    if (sortKey) {
      return clientSort([...rows], sortKey, sortDir, {
        service: (r) => (r.s.name || '').toLowerCase(),
        asset: (r) => (r.asset?.name || '').toLowerCase(),
        interval: (r) => (r.s.interval_type === 'usage_based' ? (r.s.interval_hours || 0) : (r.s.interval_days || 0)),
        next_due: (r) => r.st.urgency,
        status: (r) => r.st.status,
      });
    }
    return [...rows].sort((a, b) => a.st.urgency - b.st.urgency);
  }, [enriched, bandFilter, searchQuery, sortKey, sortDir]);

  const assetGroups = useMemo(() => {
    if (groupBy !== 'by_asset') return null;
    const map = new Map();
    visibleRows.forEach(row => {
      const key = row.s.asset_id;
      if (!map.has(key)) map.set(key, { asset: row.asset, assetId: key, rows: [], overdue: 0 });
      const g = map.get(key);
      g.rows.push(row);
      if (row.st.status === 'overdue') g.overdue++;
    });
    return [...map.values()].sort((a, b) => b.overdue - a.overdue || (a.asset?.name || '').localeCompare(b.asset?.name || ''));
  }, [visibleRows, groupBy]);

  const pagedRows = visibleRows.slice(0, pmVisible);
  useEffect(() => { setPmVisible(PM_PAGE_SIZE); }, [bandFilter, searchQuery, groupBy]);

  const raiseWO = (row) => {
    const s = row.s;
    if (s.open_work_order_id) {
      navigate(`/work-orders?view=${s.open_work_order_id}`);
    } else {
      navigate(`/work-orders?new=true&asset=${s.asset_id}&maintenance=${s.id}&title=${encodeURIComponent(s.name)}`);
    }
  };

  const toggleSelect = (row) => {
    const s = row.s;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(s.id)) {
        next.delete(s.id);
        if (next.size === 0) setSelectedAssetId(null);
      } else {
        if (selectedAssetId && s.asset_id !== selectedAssetId) {
          // switching asset — start a fresh selection on the new asset
          setSelectedAssetId(s.asset_id);
          return new Set([s.id]);
        }
        setSelectedAssetId(s.asset_id);
        next.add(s.id);
      }
      return next;
    });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setSelectedAssetId(null); };

  const bulkCreateWO = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const params = new URLSearchParams({
      new: 'true', asset: selectedAssetId,
      maintenance_ids: ids.join(','),
      title: `PM: ${ids.length} service${ids.length === 1 ? '' : 's'}`,
    });
    navigate(`/work-orders?${params.toString()}`);
  };

  const openAddToWo = async () => {
    try {
      const res = await workOrdersApi.getOpenForAsset(selectedAssetId);
      setBulkOpenWOs(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch { setBulkOpenWOs([]); }
    setAddToWoOpen(true);
  };
  const bulkAttach = async (woId) => {
    const ids = Array.from(selectedIds);
    setBulkLinking(true);
    try {
      for (const sid of ids) { await workOrdersApi.linkMaintenance(woId, sid); }
      toast.success(`Added ${ids.length} to work order`);
      setAddToWoOpen(false);
      clearSelection();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to work order');
    } finally {
      setBulkLinking(false);
    }
  };


  const filterByDate = (schedule) => {
    if (dateFilter === 'all') return true;
    if (!schedule.next_due_date) return dateFilter === 'all';

    const dueDate = parseISO(schedule.next_due_date);
    const now = new Date();

    switch (dateFilter) {
      case 'today':
        return isToday(dueDate);
      case 'this_week':
        return isThisWeek(dueDate, { weekStartsOn: 1 });
      case 'last_week':
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
      case 'this_month':
        return isThisMonth(dueDate);
      case 'last_month':
        const lastMonthStart = startOfMonth(subMonths(now, 1));
        const lastMonthEnd = endOfMonth(subMonths(now, 1));
        return isWithinInterval(dueDate, { start: lastMonthStart, end: lastMonthEnd });
      default:
        return true;
    }
  };

  const filteredSchedules = useMemo(() => {
    return schedules.filter(schedule => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === '' || 
        schedule.name.toLowerCase().includes(searchLower) ||
        getAssetName(schedule.asset_id).toLowerCase().includes(searchLower);

      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'overdue' && schedule.is_overdue) ||
        (statusFilter === 'on_track' && !schedule.is_overdue);

      const matchesDate = filterByDate(schedule);

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [schedules, searchQuery, statusFilter, dateFilter, assets]);

  const sortedSchedules = useMemo(
    () => clientSort(filteredSchedules, sortKey, sortDir, {
      name: (s) => s.name,
      asset: (s) => getAssetName(s.asset_id),
      type: (s) => s.interval_type,
      status: (s) => (s.is_overdue ? 1 : 0),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredSchedules, sortKey, sortDir]
  );

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateFilter('all');
    setLinkedFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || dateFilter !== 'all' || linkedFilter !== 'all';

  const openAddDrawer = () => {
    setSelectedSchedule(null);
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const openViewDrawer = (schedule) => {
    setSelectedSchedule(schedule);
    setDrawerMode('view');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode(null);
    setSelectedSchedule(null);
  };

  const handleFormSuccess = () => {
    closeDrawer();
    loadData();
  };

  const getDrawerTitle = () => {
    switch (drawerMode) {
      case 'add': return 'Schedule Maintenance';
      case 'view': return 'Maintenance Details';
      default: return '';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="maintenance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Preventative Maintenance
          </h1>
          <p className="text-muted-foreground">
            Schedule and track equipment maintenance
            {bandCounts.overdue > 0 && (
              <span className="text-[#B4432A] font-medium"> · {bandCounts.overdue} overdue across {overdueAssetCount} {overdueAssetCount === 1 ? 'asset' : 'assets'}</span>
            )}
          </p>
        </div>
        {isAdmin() && (
          <Button onClick={openAddDrawer} className="btn-industrial" data-testid="schedule-maintenance-btn">
            <Plus className="w-5 h-5 mr-2" />
            Schedule Maintenance
          </Button>
        )}
      </div>

      {/* Band cards — these ARE the filter. Click one to filter the table. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pm-bands">
        {[
          { id: 'overdue', label: 'Overdue', sub: 'past due', count: bandCounts.overdue, active: 'border-[#E8A093] bg-[#FDEDE8]', num: 'text-[#B4432A]' },
          { id: 'due_soon', label: 'Due within 7 days', sub: 'act this week', count: bandCounts.due_soon, active: 'border-[#EBCE6A] bg-[#FFF7DA]', num: 'text-[#7A5B00]' },
          { id: 'on_track', label: 'On track', sub: 'no action', count: bandCounts.on_track, active: 'border-[#CFC8B8] bg-[#F4F2ED]', num: 'text-[#2A2A2A]' },
          { id: 'reading', label: 'Reading out of date', sub: 'cannot assess', count: bandCounts.reading, active: 'border-[#CFC8B8] bg-[#F4F2ED]', num: 'text-[#8A857A]' },
        ].map(band => {
          const selected = bandFilter === band.id;
          return (
            <button
              key={band.id}
              type="button"
              onClick={() => setBandFilter(selected ? null : band.id)}
              data-testid={`pm-band-${band.id}`}
              className={`text-left rounded-xl border p-4 transition-colors ${selected ? band.active + ' ring-2 ring-offset-1 ring-[#C9A400]' : 'border-[#E6E2D9] bg-white hover:border-[#D4CFC2]'}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8A857A]">{band.label}</div>
              <div className={`text-[28px] leading-tight font-extrabold mt-1 ${band.num}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{band.count}</div>
              <div className="text-[12px] text-muted-foreground">{band.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Filters + list — single card (gold standard) */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by service name or asset..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="maintenance-search-input"
              />
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm text-muted-foreground" data-testid="pm-result-count">
                {visibleRows.length} of {schedules.length} schedules
              </span>
              <DensityToggle density={density} onChange={setDensity} />
              <div className="inline-flex items-center gap-1 bg-secondary rounded-md p-0.5">
                {[
                  { id: 'due_next', label: 'Due next' },
                  { id: 'by_asset', label: 'By asset' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setGroupBy(opt.id)}
                    data-testid={`pm-group-${opt.id}`}
                    className={`px-3 py-1.5 rounded text-[13px] font-semibold transition-colors ${groupBy === opt.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="py-16 text-center">
            <Settings className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No maintenance schedules</h3>
            <p className="text-muted-foreground mb-4">
              {(bandFilter || searchQuery) ? 'Nothing matches these filters.' : 'Set up your first preventative maintenance schedule.'}
            </p>
            {(bandFilter || searchQuery) ? (
              <Button variant="outline" onClick={() => { setBandFilter(null); setSearchQuery(''); }} data-testid="pm-clear-filters">
                Clear filters
              </Button>
            ) : isAdmin() && (
              <Button onClick={openAddDrawer} data-testid="empty-schedule-maintenance-btn">
                <Plus className="w-5 h-5 mr-2" />
                Schedule First Maintenance
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#FFF7D1] border-b border-[#E8D48A]" data-testid="pm-bulk-bar">
                <span className="text-sm font-semibold text-[#1A1A1A]">
                  {selectedIds.size} selected
                  <span className="text-[#8A6D00] font-normal"> · {(assets.find(a => a.id === selectedAssetId)?.unit_number || assets.find(a => a.id === selectedAssetId)?.name || 'asset')}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={clearSelection} className="text-sm text-[#8A857A] hover:text-[#1A1A1A] px-2" data-testid="pm-bulk-clear">Clear</button>
                  <Button variant="outline" size="sm" onClick={openAddToWo} data-testid="pm-bulk-add-existing">Add to open WO</Button>
                  <button onClick={bulkCreateWO} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#FFD108] text-black text-sm font-semibold hover:bg-[#EAC200] transition-colors" data-testid="pm-bulk-create-wo">
                    <Wrench className="w-4 h-4" /> Create Work Order From Selected
                  </button>
                </div>
              </div>
            )}
            <table className="data-table w-full" data-density={density}>
              <thead>
                <tr className="border-b border-border">
                  <th className="pl-4 py-4 w-8"></th>
                  <SortableTh sortKey="service" label="Service" current={sortKey} dir={sortDir} onSort={toggleSort} className="p-4" />
                  <SortableTh sortKey="asset" label="Asset" current={sortKey} dir={sortDir} onSort={toggleSort} className="p-4" />
                  <SortableTh sortKey="interval" label="Interval" current={sortKey} dir={sortDir} onSort={toggleSort} className="p-4" />
                  <SortableTh sortKey="next_due" label="Next Due" current={sortKey} dir={sortDir} onSort={toggleSort} className="p-4" />
                  <SortableTh sortKey="status" label="Status" current={sortKey} dir={sortDir} onSort={toggleSort} className="p-4" />
                  <th className="text-right p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {groupBy === 'by_asset' && assetGroups ? (
                  assetGroups.map(group => (
                    <React.Fragment key={group.assetId}>
                      <tr className="bg-[#FBFAF7] border-b border-[#E6E2D9]" data-testid={`pm-group-row-${group.assetId}`}>
                        <td colSpan={7} className="px-4 py-2">
                          <div className="flex items-center gap-2 text-[12px]">
                            <span className="font-semibold text-[#2A2A2A]">{group.asset ? (group.asset.name || group.asset.unit_number) : 'Unknown asset'}</span>
                            {group.asset?.unit_number && <span className="font-mono text-muted-foreground">{group.asset.unit_number}</span>}
                            <span className="text-muted-foreground">· {group.rows.length} {group.rows.length === 1 ? 'schedule' : 'schedules'}</span>
                            {group.overdue > 0 && <span className="text-[#B4432A] font-semibold">· {group.overdue} overdue</span>}
                          </div>
                        </td>
                      </tr>
                      {group.rows.map(row => <PmRow key={row.s.id} row={row} onOpen={openViewDrawer} onRaise={raiseWO} selected={selectedIds.has(row.s.id)} onToggleSelect={toggleSelect} selectDisabled={selectedAssetId && row.s.asset_id !== selectedAssetId} />)}
                    </React.Fragment>
                  ))
                ) : (
                  pagedRows.map(row => <PmRow key={row.s.id} row={row} onOpen={openViewDrawer} onRaise={raiseWO} selected={selectedIds.has(row.s.id)} onToggleSelect={toggleSelect} selectDisabled={selectedAssetId && row.s.asset_id !== selectedAssetId} />)
                )}
              </tbody>
            </table>
            {groupBy === 'due_next' && pagedRows.length < visibleRows.length && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-white" data-testid="pm-load-more-bar">
                <span className="text-xs text-muted-foreground font-mono">
                  Showing {pagedRows.length.toLocaleString()} of {visibleRows.length.toLocaleString()} schedules
                </span>
                <Button variant="outline" size="sm" onClick={() => setPmVisible(v => v + PM_PAGE_SIZE)} data-testid="pm-load-more-btn">
                  Load More ({(visibleRows.length - pagedRows.length).toLocaleString()} more)
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Add-to-existing-WO dialog */}
      <Dialog open={addToWoOpen} onOpenChange={setAddToWoOpen}>
        <DialogContent data-testid="pm-add-to-wo-dialog">
          <DialogHeader>
            <DialogTitle>Add {selectedIds.size} schedule{selectedIds.size === 1 ? '' : 's'} to a work order</DialogTitle>
            <DialogDescription>Pick an open work order for this asset. Each schedule's checklist is merged into it.</DialogDescription>
          </DialogHeader>
          {bulkOpenWOs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No open work orders for this asset. Use "Create Work Order" instead.</p>
          ) : (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
              {bulkOpenWOs.map(wo => (
                <button key={wo.id} onClick={() => bulkAttach(wo.id)} disabled={bulkLinking} className="text-left id-card p-3 hover:bg-[#FBFAF6] transition-colors disabled:opacity-50" data-testid={`pm-add-to-wo-${wo.id}`}>
                  <div className="font-semibold text-sm text-[#1A1A1A]">{wo.reference ? `${wo.reference} · ` : ''}{wo.title}</div>
                </button>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToWoOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slide Drawer */}
      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title={drawerMode === 'view' ? `SCHEDULE ${selectedSchedule?.reference || ''}` : getDrawerTitle()}
        titleExtra={drawerMode === 'view' && selectedSchedule?.reference ? (
          <button
            onClick={() => { navigator.clipboard?.writeText(selectedSchedule.reference); toast.success('Reference copied'); }}
            className="text-[#8A857A] hover:text-[#1A1A1A]"
            title="Copy reference"
            data-testid="pm-copy-reference"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        ) : null}
        size="lg"
        dismissable={drawerMode === 'view'}
      >
        {drawerMode === 'add' ? (
          <MaintenanceForm 
            assets={assets}
            onSuccess={handleFormSuccess}
            onCancel={closeDrawer}
          />
        ) : drawerMode === 'view' && selectedSchedule ? (
          <MaintenanceDetailView 
            schedule={selectedSchedule}
            onClose={closeDrawer}
            onDelete={() => setConfirmDeleteOpen(true)}
            onChanged={loadData}
            isAdmin={isAdmin()}
          />
        ) : null}
      </SlideDrawer>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={(o) => { if (!o) setConfirmDeleteOpen(false); }}
        title="Delete maintenance schedule?"
        description={
          selectedSchedule
            ? `"${selectedSchedule.name || 'this schedule'}" will no longer fire reminders or generate work orders.`
            : ''
        }
        footnote="Past completions and history are kept on the asset record. This action cannot be undone."
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-maintenance"
      />
    </div>
  );
};
